<template>
  <div class="cip-overlay" :style="{ zIndex }" @mousedown.self="$emit('close')">
    <div class="cip" role="dialog" aria-modal="true" aria-label="Importar contratos">
      <header class="cip__head">
        <div>
          <h2 class="cip__title">Importar contratos</h2>
          <ol class="cip__steps">
            <li v-for="(label, i) in STEPS" :key="label" :class="{ 'cip__steps--on': i === step, 'cip__steps--done': i < step }">{{ i + 1 }}. {{ label }}</li>
          </ol>
        </div>
        <button class="cip__close" aria-label="Cerrar" @click="$emit('close')"><span class="mdi mdi-close"></span></button>
      </header>

      <div class="cip__body">
        <!-- 1. Archivo -->
        <section v-if="step === 0" class="cip__section">
          <p class="cip__lead">Sube tu Excel o CSV con los contratos, o pega las celdas directamente desde Excel. Las columnas pueden venir en cualquier orden; en el siguiente paso las asocias.</p>

          <label class="cip__drop" @dragover.prevent @drop.prevent="onDrop">
            <span class="mdi mdi-file-upload-outline cip__drop-icon"></span>
            <span class="cip__drop-text">Arrastra un archivo aquí o <u>elígelo</u></span>
            <span class="cip__drop-sub">Excel (.xlsx) o CSV</span>
            <input type="file" accept=".xlsx,.xlsm,.csv,.txt" hidden @change="onPick" />
          </label>

          <div class="cip__or">o pega desde Excel</div>
          <textarea v-model="pasted" class="cip__paste" rows="5" placeholder="Copia las celdas en Excel (con los encabezados) y pégalas aquí" @input="onPaste"></textarea>

          <button class="cip__link" @click="downloadTemplate"><span class="mdi mdi-download"></span> Descargar plantilla de ejemplo</button>
          <p v-if="error" class="cip__error">{{ error }}</p>
        </section>

        <!-- 2. Columnas -->
        <section v-else-if="step === 1" class="cip__section">
          <p class="cip__lead">Revisa qué columna de tu archivo corresponde a cada dato. Detectamos las que pudimos; corrige las que falten. Los campos con * son obligatorios.</p>
          <label class="cip__check"><input v-model="hasHeader" type="checkbox" @change="remap" /> La primera fila son encabezados</label>

          <table class="cip__map">
            <thead><tr><th>Dato en Parkos</th><th>Columna de tu archivo</th><th>Ejemplo</th></tr></thead>
            <tbody>
              <tr v-for="f in IMPORT_FIELDS" :key="f.key" :class="{ 'cip__map--missing': f.required && mapping[f.key] < 0 }">
                <td>{{ f.label }}<span v-if="f.required"> *</span></td>
                <td>
                  <select v-model.number="mapping[f.key]" class="cip__select">
                    <option :value="-1">— No importar —</option>
                    <option v-for="(h, i) in columnNames" :key="i" :value="i">{{ h }}</option>
                  </select>
                </td>
                <td class="cip__sample">{{ sample(f.key) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="missingRequired.length" class="cip__error">Falta asociar: {{ missingRequired.join(', ') }}.</p>
          <p class="cip__hint">{{ bodyRows.length }} fila{{ bodyRows.length === 1 ? '' : 's' }} detectada{{ bodyRows.length === 1 ? '' : 's' }}.</p>
        </section>

        <!-- 3. Revisión -->
        <section v-else-if="step === 2" class="cip__section">
          <div v-if="busy" class="cip__busy">Revisando el archivo…</div>
          <template v-else>
            <div class="cip__summary">
              <span class="cip__pill cip__pill--ok">{{ preview.valid }} lista{{ preview.valid === 1 ? '' : 's' }}</span>
              <span v-if="preview.invalid" class="cip__pill cip__pill--err">{{ preview.invalid }} con errores</span>
              <label v-if="preview.invalid" class="cip__check"><input v-model="onlyErrors" type="checkbox" /> Ver solo las filas con errores</label>
            </div>
            <div class="cip__scroll">
              <table class="cip__table">
                <thead><tr><th></th><th>Nave</th><th>Inquilino</th><th>Renta</th><th>Inicio</th><th>Vencimiento</th><th>Observaciones</th></tr></thead>
                <tbody>
                  <tr v-for="r in shownRows" :key="r.index" :class="{ 'cip__row--err': !r.ok }">
                    <td><span class="mdi" :class="r.ok ? 'mdi-check-circle cip__ok' : 'mdi-alert-circle cip__bad'"></span></td>
                    <td>{{ payloadRows[r.index].plot }}</td>
                    <td>{{ payloadRows[r.index].tenant }}</td>
                    <td>{{ payloadRows[r.index].rent }}</td>
                    <td>{{ payloadRows[r.index].startDate }}</td>
                    <td>{{ payloadRows[r.index].endDate }}</td>
                    <td>
                      <div v-for="e in r.errors" :key="e" class="cip__err-line">{{ e }}</div>
                      <div v-for="w in r.warnings" :key="w" class="cip__warn-line">{{ w }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="cip__hint">Las naves deben existir ya en el parque. Los inquilinos nuevos se crean con su contacto de pagos; si ya existen en el parque, se reutilizan.</p>
          </template>
          <p v-if="error" class="cip__error">{{ error }}</p>
        </section>

        <!-- 4. Resultado -->
        <section v-else class="cip__section cip__done">
          <span class="mdi mdi-check-circle-outline cip__done-icon"></span>
          <h3>{{ result.imported }} contrato{{ result.imported === 1 ? '' : 's' }} importado{{ result.imported === 1 ? '' : 's' }}</h3>
          <p v-if="result.skipped">{{ result.skipped }} fila{{ result.skipped === 1 ? '' : 's' }} no se importó{{ result.skipped === 1 ? '' : 'aron' }}. Puedes corregirlas en tu archivo y volver a importar solo esas.</p>
          <p>La cobranza de estos contratos sigue la fecha de arranque del parque. Para marcar meses anteriores como no pagados, abre el contrato y revisa «Anterior a Parkos».</p>
        </section>
      </div>

      <footer class="cip__foot">
        <button v-if="step > 0 && step < 3" class="cip__btn cip__btn--ghost" :disabled="busy" @click="back">Atrás</button>
        <span class="cip__spacer"></span>
        <button v-if="step === 1" class="cip__btn" :disabled="missingRequired.length || !bodyRows.length" @click="goPreview">Revisar</button>
        <button v-if="step === 2 && !busy" class="cip__btn" :disabled="!preview.valid || importing" @click="doImport">
          {{ importing ? 'Importando…' : preview.invalid ? `Importar ${preview.valid} válidas` : `Importar ${preview.valid} contrato${preview.valid === 1 ? '' : 's'}` }}
        </button>
        <button v-if="step === 3" class="cip__btn" @click="finish">Listo</button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { readTableFile, parseDelimited } from '@/utils/tableFile'
import { IMPORT_FIELDS, autoMap, buildRows, templateCsv } from './importMapping'
import { previewContractImport, runContractImport } from '@/services/contractImport'

const props = defineProps({
  parkId: { type: String, required: true },
  // El onboarding se muestra a pantalla completa por encima de todo; ahí se sube la capa.
  zIndex: { type: Number, default: 1000 },
})
const emit = defineEmits(['close', 'imported'])

const STEPS = ['Archivo', 'Columnas', 'Revisión', 'Listo']
const step = ref(0)
const error = ref('')
const busy = ref(false)
const importing = ref(false)

const table = ref([])
const pasted = ref('')
const hasHeader = ref(true)
const mapping = reactive(Object.fromEntries(IMPORT_FIELDS.map((f) => [f.key, -1])))

const preview = ref({ rows: [], valid: 0, invalid: 0 })
const payloadRows = ref([])
const onlyErrors = ref(false)
const result = ref({ imported: 0, skipped: 0 })

const columnNames = computed(() => {
  const width = Math.max(...table.value.map((r) => r.length), 0)
  const head = hasHeader.value ? table.value[0] || [] : []
  return Array.from({ length: width }, (_, i) => (hasHeader.value && head[i] ? head[i] : `Columna ${i + 1}`))
})
const bodyRows = computed(() => (hasHeader.value ? table.value.slice(1) : table.value))
const missingRequired = computed(() => IMPORT_FIELDS.filter((f) => f.required && !(mapping[f.key] >= 0)).map((f) => f.label))
const shownRows = computed(() => (onlyErrors.value ? preview.value.rows.filter((r) => !r.ok) : preview.value.rows))

function sample(key) {
  const i = mapping[key]
  if (!(i >= 0)) return ''
  const row = bodyRows.value.find((r) => (r[i] ?? '') !== '')
  return row ? row[i] : ''
}

function remap() {
  const detected = autoMap(hasHeader.value ? table.value[0] || [] : [])
  for (const f of IMPORT_FIELDS) mapping[f.key] = detected[f.key]
}

function acceptTable(rows) {
  error.value = ''
  if (!rows.length || rows.length < 2) {
    error.value = 'No encontramos filas con datos. El archivo debe traer los encabezados y al menos un contrato.'
    return
  }
  table.value = rows
  // Si ninguna celda de la primera fila se parece a un encabezado conocido, probablemente no trae encabezados.
  const detected = autoMap(rows[0])
  hasHeader.value = IMPORT_FIELDS.some((f) => detected[f.key] >= 0)
  remap()
  step.value = 1
}

async function loadFile(file) {
  if (!file) return
  try {
    acceptTable(await readTableFile(file))
  } catch (err) {
    error.value = err.message || 'No se pudo leer el archivo'
  }
}
const onPick = (e) => loadFile(e.target.files[0])
const onDrop = (e) => loadFile(e.dataTransfer.files[0])
function onPaste() {
  if (pasted.value.trim().includes('\n')) acceptTable(parseDelimited(pasted.value))
}

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([templateCsv()], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'plantilla-contratos-parkos.csv'
  a.click()
  URL.revokeObjectURL(url)
}

async function goPreview() {
  step.value = 2
  busy.value = true
  error.value = ''
  onlyErrors.value = false
  try {
    payloadRows.value = buildRows(table.value, mapping, hasHeader.value)
    preview.value = await previewContractImport(props.parkId, payloadRows.value)
  } catch (err) {
    error.value = err.message
    preview.value = { rows: [], valid: 0, invalid: 0 }
  } finally {
    busy.value = false
  }
}

async function doImport() {
  importing.value = true
  error.value = ''
  try {
    result.value = await runContractImport(props.parkId, payloadRows.value, preview.value.invalid > 0)
    step.value = 3
  } catch (err) {
    error.value = err.message
  } finally {
    importing.value = false
  }
}

function back() {
  error.value = ''
  step.value -= 1
}
function finish() {
  emit('imported', result.value.imported)
  emit('close')
}
</script>

<style scoped>
.cip-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
.cip { width: min(920px, 100%); max-height: 90vh; background: #fff; border-radius: 12px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25); font-family: var(--font-body, Inter, sans-serif); }
.cip__head { display: flex; justify-content: space-between; align-items: flex-start; padding: 16px 20px 12px; border-bottom: 1px solid #f1f5f9; }
.cip__title { margin: 0 0 6px; font-size: 16px; font-weight: 600; color: #1e3a5f; }
.cip__steps { display: flex; gap: 14px; margin: 0; padding: 0; list-style: none; font-size: 11px; color: #94a3b8; }
.cip__steps--on { color: #3b82f6; font-weight: 600; }
.cip__steps--done { color: #10b981; }
.cip__close { border: none; background: none; font-size: 18px; color: #94a3b8; cursor: pointer; }
.cip__body { padding: 16px 20px; overflow-y: auto; flex: 1; min-height: 0; }
.cip__section { display: flex; flex-direction: column; gap: 12px; }
.cip__lead { margin: 0; font-size: 13px; line-height: 1.5; color: #475569; }
.cip__drop { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 26px 12px; border: 2px dashed #bfdbfe; border-radius: 10px; background: #f8fafc; cursor: pointer; }
.cip__drop-icon { font-size: 30px; color: #3b82f6; }
.cip__drop-text { font-size: 13px; color: #1e3a5f; }
.cip__drop-sub { font-size: 11px; color: #94a3b8; }
.cip__or { text-align: center; font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; }
.cip__paste { width: 100%; box-sizing: border-box; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; font: 12px var(--font-mono); resize: vertical; }
.cip__link { align-self: flex-start; border: none; background: none; color: #3b82f6; font-size: 12px; cursor: pointer; padding: 0; }
.cip__check { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #475569; }
.cip__map { width: 100%; border-collapse: collapse; font-size: 12px; }
.cip__map th { text-align: left; font-size: 10px; text-transform: uppercase; color: #94a3b8; padding: 4px 8px; }
.cip__map td { padding: 5px 8px; border-top: 1px solid #f1f5f9; color: #1e3a5f; }
.cip__map--missing td:first-child { color: #dc2626; font-weight: 600; }
.cip__select { width: 100%; height: 30px; border: 1px solid #e2e8f0; border-radius: 6px; padding: 0 6px; font-size: 12px; background: #fff; }
.cip__sample { color: #94a3b8; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cip__summary { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.cip__pill { padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.cip__pill--ok { background: #dcfce7; color: #15803d; }
.cip__pill--err { background: #fee2e2; color: #b91c1c; }
.cip__scroll { overflow: auto; max-height: 48vh; border: 1px solid #f1f5f9; border-radius: 8px; }
.cip__table { width: 100%; border-collapse: collapse; font-size: 12px; }
.cip__table th { position: sticky; top: 0; background: #f8fafc; text-align: left; font-size: 10px; text-transform: uppercase; color: #94a3b8; padding: 6px 8px; }
.cip__table td { padding: 6px 8px; border-top: 1px solid #f1f5f9; vertical-align: top; color: #1e3a5f; }
.cip__row--err { background: #fffbfb; }
.cip__ok { color: #10b981; }
.cip__bad { color: #dc2626; }
.cip__err-line { color: #b91c1c; }
.cip__warn-line { color: #b45309; }
.cip__hint { margin: 0; font-size: 11px; color: #94a3b8; }
.cip__error { margin: 0; font-size: 12px; color: #dc2626; }
.cip__busy { padding: 30px; text-align: center; color: #64748b; font-size: 13px; }
.cip__done { align-items: center; text-align: center; padding: 24px 0; }
.cip__done-icon { font-size: 44px; color: #10b981; }
.cip__done h3 { margin: 0; color: #1e3a5f; }
.cip__done p { margin: 0; max-width: 520px; font-size: 13px; color: #64748b; line-height: 1.5; }
.cip__foot { display: flex; align-items: center; gap: 10px; padding: 12px 20px; border-top: 1px solid #f1f5f9; }
.cip__spacer { flex: 1; }
.cip__btn { height: 34px; padding: 0 18px; border: none; border-radius: 6px; background: #3b82f6; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; }
.cip__btn:disabled { opacity: 0.45; cursor: default; }
.cip__btn--ghost { background: #fff; color: #475569; border: 1px solid #e2e8f0; }
</style>